import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../environment/environment';
import { AuthService } from '../services/auth.service';

export interface CashbackRequest {
  cashbackId: string;
  application: string;
  amount: string;
  approvedAmount?: string;
  orderDate: string;
  approved: string;  // 'pending', 'approved', 'rejected'
  phoneNumber?: string;
  displayName?: string;
  filePath?: string;
  imageUrl?: string;
  uid: string;
  description?: string;
  adminNotes?: string;
  createdAt?: string;
  updatedAt?: string;
  reviewedBy?: string;
  reviewedAt?: string;
}

export interface CashbackRequestResponse {
  success: boolean;
  message?: string;
  requests?: CashbackRequest[];
  data?: any;
}

@Injectable({
  providedIn: 'root'
})
export class CashbackRequestService {
  private readonly apiUrl = environment.baseUrl;

  constructor(
    private http: HttpClient,
    private authService: AuthService
  ) { }

  private getHttpHeaders(): HttpHeaders {
    const token = this.authService.idToken;
    let headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });
    
    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }
    
    return headers;
  }

  /**
   * Submit a new cashback request
   */
  submitRequest(requestData: any): Observable<CashbackRequestResponse> {
    return this.http.post<CashbackRequestResponse>(
      `${this.apiUrl}/cashback/user`,
      requestData,
      { headers: this.getHttpHeaders() }
    ).pipe(
      map(response => ({
        success: true,
        message: 'Request submitted successfully',
        data: response
      })),
      catchError(error => {
        console.error('Error submitting cashback request:', error);
        throw error;
      })
    );
  }

  /**
   * Get all cashback requests for the current user
   */
  getMyRequests(userId: string): Observable<CashbackRequestResponse> {
    // Use both uid and userId params to ensure backend compatibility
    return this.http.get<any>(
      `${this.apiUrl}/cashback/user?uid=${userId}&userId=${userId}`,
      { headers: this.getHttpHeaders() }
    ).pipe(
      map(response => {
        // Handle different response formats
        let requests: CashbackRequest[] = [];
        
        if (response.items) {
          requests = response.items.items || response.items;
        } else if (response.requests) {
          requests = response.requests;
        } else if (Array.isArray(response)) {
          requests = response;
        }
        
        // Filter requests for this user and sort by date
        requests = requests
          .filter((req: CashbackRequest) => req.uid === userId)
          .sort((a: CashbackRequest, b: CashbackRequest) => {
            const dateA = new Date(a.createdAt || a.orderDate);
            const dateB = new Date(b.createdAt || b.orderDate);
            return dateB.getTime() - dateA.getTime();
          });
        
        return {
          success: true,
          requests: requests
        };
      }),
      catchError(error => {
        console.error('Error fetching cashback requests:', error);
        return of({
          success: false,
          requests: [],
          message: 'Failed to load requests'
        });
      })
    );
  }

  /**
   * Upload image to S3 and return the URL
   * Note: This requires a pre-signed URL endpoint or direct S3 upload configuration
   */
  async uploadImage(file: File, userId: string): Promise<string> {
    // Create form data for upload
    const formData = new FormData();
    formData.append('file', file);
    formData.append('userId', userId);
    formData.append('type', 'cashback-proof');
    
    try {
      // Option 1: Upload to a dedicated file upload endpoint
      const response = await this.http.post<any>(
        `${this.apiUrl}/upload/image`,
        formData,
        {
          headers: new HttpHeaders({
            'Authorization': `Bearer ${this.authService.idToken}`
            // Don't set Content-Type - let browser set it with boundary for FormData
          })
        }
      ).toPromise();
      
      return response.imageUrl || response.url || '';
      
    } catch (error) {
      console.error('Error uploading image:', error);
      
      // Fallback: Convert to base64 and include in request
      // This is not ideal for large files but works as a fallback
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          // Return base64 data URL as fallback
          resolve(reader.result as string);
        };
        reader.onerror = () => reject('Failed to read file');
        reader.readAsDataURL(file);
      });
    }
  }

  /**
   * Get all pending cashback requests (admin use)
   */
  getPendingRequests(): Observable<CashbackRequestResponse> {
    return this.http.get<any>(
      `${this.apiUrl}/cashback/user?status=pending`,
      { headers: this.getHttpHeaders() }
    ).pipe(
      map(response => {
        let requests: CashbackRequest[] = [];
        
        if (response.items) {
          requests = response.items.items || response.items;
        } else if (response.requests) {
          requests = response.requests;
        }
        
        // Filter only pending requests
        requests = requests.filter((req: CashbackRequest) => req.approved === 'pending');
        
        return {
          success: true,
          requests: requests
        };
      }),
      catchError(error => {
        console.error('Error fetching pending requests:', error);
        return of({
          success: false,
          requests: []
        });
      })
    );
  }
}
